import { createFileRoute, notFound, redirect } from '@tanstack/react-router';

import type { TLAvailableNodesResponse } from '@/api/gen/tessera-backend-api.schemas';
import { getGetWorkflowNodesV2QueryKey } from '@/api/gen/workflows-v2-nodes';
import { DocPage } from '@/components/features/documentation/DocPage';
import { LoomNodeDocPage } from '@/components/features/documentation/fetched-docs/LoomNodeDocPage';
import { getDocComponent } from '@/features/documentation/content-registry';
import { DOCS_TREE } from '@/features/documentation/utils/docs-tree';
import { canViewDoc, resolveDocPath } from '@/features/documentation/utils/docs-tree-utils';
import { mergeLoomNodeDocs } from '@/features/documentation/utils/loom-node-docs-tree';

export const Route = createFileRoute('/_protected/t/$tenantId/docs/$')({
  beforeLoad: ({ params, context: { user, devTools, queryClient } }) => {
    // Read, not fetch: the parent route has already warmed this query, and a
    // beforeLoad that awaited it again would stall every documentation page.
    const nodes = queryClient.getQueryData<TLAvailableNodesResponse>(getGetWorkflowNodesV2QueryKey())?.nodes ?? [];
    const chain = resolveDocPath(params._splat ?? '', mergeLoomNodeDocs(DOCS_TREE, nodes));
    const node = chain?.at(-1);
    if (!chain || !node) throw notFound();
    if (!node.contentPath && !node.nodeName) throw notFound();
    if (!canViewDoc(user, chain, devTools))
      throw redirect({ to: '/t/$tenantId/docs', params: { tenantId: params.tenantId } });
    return { docContentPath: node.contentPath, docNodeName: node.nodeName };
  },
  component: DocumentationPage,
});

function DocumentationPage() {
  const { docContentPath, docNodeName } = Route.useRouteContext();

  if (docNodeName) return <LoomNodeDocPage nodeName={docNodeName} />;

  const Doc = docContentPath ? getDocComponent(docContentPath) : undefined;
  if (!Doc) return null;
  return <DocPage Doc={Doc} />;
}
